import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash, ArrowSquareOut, MapPin, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { TIPO_VINCULO_AUT, CATEGORIAS_AUTH } from "../lib/constants";
import LocationSelector, { LocationFilter } from "../components/LocationSelector";
import MapPicker from "../components/MapPicker";
import { StatusBadge } from "../components/Semaforo";
import { useConfirm } from "../components/ConfirmDialog";

export default function AuthoritiesList({ entityType, title, description, detailRoute }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [filters, setFilters] = useState({ q: "", region: "", provincia: "", comuna: "", categoria: "" });
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const categoriasForType = CATEGORIAS_AUTH[entityType] || [];

  function emptyForm() {
    return {
      entity_type: entityType, name: "", tipo_vinculo: "Administrativo",
      categoria: "", description: "",
      location: { pais: "Chile", region: "", provincia: "", comuna: "", localizacion: "", latitude: "", longitude: "" },
    };
  }

  const load = async () => {
    const params = { entity_type: entityType };
    if (filters.q) params.q = filters.q;
    if (filters.region) params.region = filters.region;
    if (filters.comuna) params.comuna = filters.comuna;
    if (filters.categoria) params.categoria = filters.categoria;
    const { data } = await api.get("/authorities", { params });
    setItems(data);
    setPage(1);
  };

  useEffect(() => {
    setFilters((f) => ({ ...f, categoria: "" }));
    load();
    /* eslint-disable-next-line */
  }, [entityType]);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const pageItems = useMemo(
    () => items.slice((page - 1) * pageSize, page * pageSize),
    [items, page],
  );
  const rangeFrom = items.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, items.length);

  const submit = async () => {
    try {
      const payload = { ...form };
      payload.location.latitude = payload.location.latitude ? +payload.location.latitude : null;
      payload.location.longitude = payload.location.longitude ? +payload.location.longitude : null;
      await api.post("/authorities", payload);
      toast.success("Creado");
      setOpen(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Eliminar registro", description: "¿Deseas eliminar este registro? Esta acción no se puede deshacer.", danger: true }))) return;
    await api.delete(`/authorities/${id}`); load();
  };

  return (
    <div data-testid={`authorities-${entityType}-page`}>
      <PageHeader
        title={title}
        description={description}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid={`new-authority-${entityType}-btn`} className="bg-slate-900 hover:bg-slate-800 text-white"><Plus weight="bold" /> Nueva</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>Nueva entidad</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nombre</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name-input" />
                </div>
                <div>
                  <Label>Tipo de vínculo</Label>
                  <Select value={form.tipo_vinculo} onValueChange={(v) => setForm({ ...form, tipo_vinculo: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPO_VINCULO_AUT.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {categoriasForType.length > 0 && (
                  <div>
                    <Label>Categoría</Label>
                    <Select
                      value={form.categoria || undefined}
                      onValueChange={(v) => setForm({ ...form, categoria: v })}
                    >
                      <SelectTrigger data-testid="auth-categoria-select"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                      <SelectContent>
                        {categoriasForType.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <LocationSelector
                  value={{ region: form.location.region, provincia: form.location.provincia, comuna: form.location.comuna }}
                  onChange={(next) => setForm({ ...form, location: { ...form.location, ...next } })}
                  testidPrefix="auth-loc"
                />
                <div className="col-span-2">
                  <Label className="mb-1.5 block">Ubicación geográfica</Label>
                  <MapPicker
                    value={{ latitude: form.location.latitude, longitude: form.location.longitude }}
                    onChange={(next) => setForm({ ...form, location: { ...form.location, latitude: next.latitude, longitude: next.longitude } })}
                    autoQuery={[form.location.comuna, form.location.region, "Chile"].filter(Boolean).join(", ")}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Descripción</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={submit} className="bg-slate-900 text-white" data-testid="save-authority-btn">Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="px-6 py-6 space-y-4">
        <div className="flex flex-wrap items-end gap-3 rounded-md border border-slate-200 bg-white p-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Buscar</Label>
            <Input placeholder="Nombre…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          </div>
          <LocationFilter
            region={filters.region}
            provincia={filters.provincia}
            comuna={filters.comuna}
            includeProvincia
            onChange={(next) => setFilters({ ...filters, ...next })}
          />
          {categoriasForType.length > 0 && (
            <div className="min-w-[180px]">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Categoría</Label>
              <Select
                value={filters.categoria || "__all__"}
                onValueChange={(v) => setFilters({ ...filters, categoria: v === "__all__" ? "" : v })}
              >
                <SelectTrigger data-testid="categoria-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas</SelectItem>
                  {categoriasForType.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button variant="secondary" onClick={load}>Aplicar</Button>
        </div>

        <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Vínculo</TableHead>
                <TableHead>Región / Comuna</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-10">Sin registros.</TableCell></TableRow>}
              {pageItems.map(a => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">
                    <Link to={`${detailRoute}/${a.id}`} className="hover:underline">{a.name}</Link>
                    {a.localizacion && <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={12} />{a.localizacion}</div>}
                  </TableCell>
                  <TableCell>
                    {a.categoria ? (
                      <span
                        data-testid={`categoria-cell-${a.id}`}
                        className="inline-flex items-center text-xs font-medium px-2 py-1 rounded bg-slate-100 text-slate-700"
                      >
                        {a.categoria}
                      </span>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                  <TableCell><StatusBadge tone={a.tipo_vinculo === "Administrativo" ? "info" : "neutral"}>{a.tipo_vinculo}</StatusBadge></TableCell>
                  <TableCell className="text-sm text-slate-600">{a.region || "—"} <span className="text-xs text-slate-400">{a.comuna || ""}</span></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Link to={`${detailRoute}/${a.id}`}><Button size="sm" variant="ghost"><ArrowSquareOut size={16} /></Button></Link>
                      <Button size="sm" variant="ghost" onClick={() => remove(a.id)}><Trash size={16} className="text-red-600" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {items.length > 0 && (
            <div
              className="flex items-center justify-between border-t border-slate-200 px-4 py-3 bg-slate-50"
              data-testid="authorities-pagination"
            >
              <div className="text-xs text-slate-600">
                Mostrando <span className="font-semibold text-slate-900">{rangeFrom}–{rangeTo}</span>
                {" "}de <span className="font-semibold text-slate-900">{items.length}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="page-prev-btn"
                >
                  <CaretLeft size={14} />
                </Button>
                <span className="text-xs text-slate-600 px-2" data-testid="page-indicator">
                  Página {page} de {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="page-next-btn"
                >
                  <CaretRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
